import { format } from 'date-fns';
import type { Trade } from '../../types';
import { getTradeUtcDate, getIanaFromGlobalTimezone, formatToLocalDisplayString } from '../timezoneUtils';
import { resolveTradingSessions, type StoredTradingSession } from '../../config/tradingSessions';

/**
 * Convertit une date de n'importe quel format en objet Date
 * Pour l'affichage avec décalage horaire, utiliser formatTradeDate() à la place
 */
export const getTradeDate = (trade: Trade, account?: any): Date | null => {
  if (!trade.openDateTime) return null;

  try {
    // Timestamp Firestore
    if (typeof trade.openDateTime === 'object' && trade.openDateTime !== null && 'seconds' in trade.openDateTime) {
      return new Date((trade.openDateTime as any).seconds * 1000);
    }

    // String ISO
    if (typeof trade.openDateTime === 'string') {
      const date = new Date(trade.openDateTime);
      return isNaN(date.getTime()) ? null : date;
    }

    // Objet Date
    if (trade.openDateTime && Object.prototype.toString.call(trade.openDateTime) === '[object Date]') {
      return trade.openDateTime as Date;
    }
  } catch (error) {
    // console.error supprimé
  }

  return null;
};

/**
 * Convertit une date de fermeture de n'importe quel format en objet Date
 * Pour l'affichage avec décalage horaire, utiliser formatTradeCloseDate() à la place
 */
export const getTradeCloseDate = (trade: Trade, account?: any): Date | null => {
  if (!trade.closeDateTime) return null;

  try {
    // Timestamp Firestore
    if (typeof trade.closeDateTime === 'object' && trade.closeDateTime !== null && 'seconds' in trade.closeDateTime) {
      return new Date((trade.closeDateTime as any).seconds * 1000);
    }

    // String ISO
    if (typeof trade.closeDateTime === 'string') {
      const date = new Date(trade.closeDateTime);
      return isNaN(date.getTime()) ? null : date;
    }

    // Objet Date
    if (trade.closeDateTime && Object.prototype.toString.call(trade.closeDateTime) === '[object Date]') {
      return trade.closeDateTime as Date;
    }
  } catch (error) {
    // console.error supprimé
  }

  return null;
};

/**
 * Détermine la session de trading basée sur l'heure UTC réelle du trade.
 * Utilise getTradeUtcDate (même logique que TradingSessionCard) pour garantir
 * la cohérence badge ↔ modal, sans dépendance DST.
 */
export const getTradingSession = (trade: Trade, account?: any, storedSessions?: StoredTradingSession[] | null): string => {
  const utcDate = getTradeUtcDate(trade, account);
  if (!utcDate) return 'Hors Killzone';

  const utcHour = utcDate.getUTCHours() + utcDate.getUTCMinutes() / 60;
  const isOpen = (start: number, end: number) =>
    end > start ? utcHour >= start && utcHour < end : utcHour >= start || utcHour < end;
  // La dernière session configurée gagne dans une zone de chevauchement : Asia → London
  // → New York reproduit le découpage historique et rend les ajouts utilisateur prévisibles.
  const matching = resolveTradingSessions(storedSessions).filter(session => isOpen(session.utcStart, session.utcEnd));
  return matching[matching.length - 1]?.name ?? 'Hors Killzone';
};

const SESSION_BADGE_THEMES: Record<string, React.CSSProperties> = {
  London: {
    backgroundColor: 'rgb(165 228 255 / 18%)',
    border: 'none',
  },
  Asia: {
    backgroundColor: 'rgb(168 85 247 / 16%)',
    border: 'none',
  },
  'New York': {
    backgroundColor: 'rgb(239 68 68 / 16%)',
    border: 'none',
  },
  'Hors Killzone': {
    backgroundColor: 'rgb(148 163 184 / 14%)',
    border: 'none',
  },
};

type RgbaColor = { red: number; green: number; blue: number; alpha: number };

function parseBadgeColor(backgroundColor: unknown): RgbaColor | null {
  if (typeof backgroundColor !== 'string') return null;
  const value = backgroundColor.trim();
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value);
  if (hex) {
    const raw = hex[1];
    const expanded = raw.length <= 4 ? raw.split('').map(channel => channel + channel).join('') : raw;
    return {
      red: parseInt(expanded.slice(0, 2), 16),
      green: parseInt(expanded.slice(2, 4), 16),
      blue: parseInt(expanded.slice(4, 6), 16),
      alpha: expanded.length === 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1,
    };
  }

  const rgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*(?:,|\/)\s*([\d.]+%?))?\s*\)$/i.exec(value);
  if (!rgb) return null;
  const rawAlpha = rgb[4];
  const alpha = rawAlpha?.endsWith('%') ? Number(rawAlpha.slice(0, -1)) / 100 : Number(rawAlpha ?? 1);
  const [red, green, blue] = rgb.slice(1, 4).map(Number);
  if (![red, green, blue, alpha].every(Number.isFinite)) return null;
  return { red, green, blue, alpha: Math.max(0, Math.min(1, alpha)) };
}

function relativeLuminance([red, green, blue]: [number, number, number]): number {
  const linear = [red, green, blue].map(channel => {
    const normalized = Math.max(0, Math.min(255, channel)) / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
}

function contrastRatio(first: [number, number, number], second: [number, number, number]): number {
  const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Calcule le contraste après avoir composé la couleur (et son opacité) sur le fond
 * réel de chaque thème. Un blanc opaque reçoit donc du texte sombre, tandis qu'un
 * blanc à 16 % sur un thème sombre conserve un texte blanc lisible.
 */
function getBadgeTextColor(backgroundColor: unknown, theme: 'light' | 'dark'): string {
  const color = parseBadgeColor(backgroundColor);
  if (!color) return theme === 'dark' ? '#fff' : '#454653';
  const canvas: [number, number, number] = theme === 'dark' ? [10, 10, 12] : [255, 255, 255];
  const visible: [number, number, number] = [
    color.red * color.alpha + canvas[0] * (1 - color.alpha),
    color.green * color.alpha + canvas[1] * (1 - color.alpha),
    color.blue * color.alpha + canvas[2] * (1 - color.alpha),
  ];
  const white: [number, number, number] = [255, 255, 255];
  const charcoal: [number, number, number] = [69, 70, 83];
  return contrastRatio(visible, white) >= contrastRatio(visible, charcoal) ? '#fff' : '#454653';
}

function getBadgeBorder(backgroundColor: unknown, theme: 'light' | 'dark'): string {
  const color = parseBadgeColor(backgroundColor);
  if (!color) return 'none';
  const canvas: [number, number, number] = theme === 'dark' ? [10, 10, 12] : [255, 255, 255];
  const visible: [number, number, number] = [
    color.red * color.alpha + canvas[0] * (1 - color.alpha),
    color.green * color.alpha + canvas[1] * (1 - color.alpha),
    color.blue * color.alpha + canvas[2] * (1 - color.alpha),
  ];
  // Une frontière discrète seulement quand le badge se fond visuellement dans sa carte.
  return contrastRatio(visible, canvas) < 1.03 ? '1px solid #00000014' : 'none';
}

/** Libellé badge session — toujours en anglais pour les killzones principales. */
export function getSessionDisplayLabel(session: string, _storedSessions?: StoredTradingSession[] | null): string {
  if (session !== 'Hors Killzone') return session;
  return 'Outside Killzone';
}

/**
 * Obtient la couleur du badge de session
 */
export const getSessionBadgeColor = (_session: string): string => {
  return '';
};

/**
 * Obtient le style background du badge de session
 */
export const getSessionBadgeStyle = (session: string, storedSessions?: StoredTradingSession[] | null): React.CSSProperties => {
  const configured = resolveTradingSessions(storedSessions).find(item => item.name === session);
  const theme = configured
    ? { backgroundColor: configured.color, border: 'none' }
    : (SESSION_BADGE_THEMES[session] ?? SESSION_BADGE_THEMES['Hors Killzone']);
  const { border: _border, ...surface } = theme;
  return {
    ...surface,
    '--session-badge-text-light': getBadgeTextColor(theme.backgroundColor, 'light'),
    '--session-badge-text-dark': getBadgeTextColor(theme.backgroundColor, 'dark'),
    '--session-badge-border-light': getBadgeBorder(theme.backgroundColor, 'light'),
    '--session-badge-border-dark': getBadgeBorder(theme.backgroundColor, 'dark'),
  } as React.CSSProperties;
};

/**
 * Calcule la complétude d'un trade
 */
export const getTradeCompleteness = (trade: Trade): { status: 'complete' | 'partial' | 'incomplete', percentage: number } => {
  let score = 0;
  const fields = [
    'asset', 'type', 'entryPrice', 'stopLoss', 'takeProfit',
    'lotSize', 'riskPercentage', 'mood', 'openDateTime'
  ];

  fields.forEach(field => {
    if (trade[field as keyof Trade] !== undefined && trade[field as keyof Trade] !== null && trade[field as keyof Trade] !== '') {
      score++;
    }
  });

  const percentage = (score / fields.length) * 100;

  if (percentage >= 100) return { status: 'complete', percentage };
  if (percentage >= 70) return { status: 'partial', percentage };
  return { status: 'incomplete', percentage };
};

const finiteNumber = (value: unknown): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const getTradeNetPnL = (trade: Trade): number =>
  finiteNumber(trade.profitLoss) + finiteNumber(trade.commission) + finiteNumber(trade.swap);

const isNearZeroPnL = (value: number): boolean => Math.abs(value) < 0.01;

const resolveBreakEvenBalance = (trade: Trade, selectedAccount?: any): number => {
  const tradeBalance = Number(trade.accountBalance);
  if (Number.isFinite(tradeBalance) && tradeBalance > 0) {
    return tradeBalance;
  }
  if (typeof selectedAccount === 'number' && Number.isFinite(selectedAccount) && selectedAccount > 0) {
    return selectedAccount;
  }
  // A threshold must not move as the account grows. Historical trades without
  // accountBalance therefore use the account's opening capital, never its
  // current balance.
  const initialBalance = Number(selectedAccount?.initialBalance);
  if (Number.isFinite(initialBalance) && initialBalance > 0) {
    return initialBalance;
  }
  const currentBalance = Number(selectedAccount?.currentBalance);
  return Number.isFinite(currentBalance) && currentBalance > 0 ? currentBalance : 0;
};

/**
 * Détermine si un trade est break-even
 */
export const isBreakEven = (
  trade: Trade,
  preferences?: any,
  selectedAccount?: any
): boolean => {
  try {
    const normalizedOutcome = String(trade.outcome || '').toUpperCase().replace(/[\s-]+/g, '_');
    if (['BREAK_EVEN', 'BREAKEVEN', 'BE', 'NEUTRAL'].includes(normalizedOutcome)) {
      return true;
    }
    const totalPnL = getTradeNetPnL(trade);

    // Si les préférences ne sont pas disponibles ou break even n'est pas configuré,
    // utiliser la logique simple (tolérance de 1 centime)
    if (!preferences?.tradeForm?.breakEven) {
      return isNearZeroPnL(totalPnL);
    }

    // Si la fonctionnalité de break even n'est pas activée, un P/L nul = break even
    if (!preferences.tradeForm.breakEven.enabled) {
      return isNearZeroPnL(totalPnL);
    }

    // Si le P/L net est nul, c'est un break even
    if (isNearZeroPnL(totalPnL)) {
      return true;
    }

    if (totalPnL > 0 && preferences.tradeForm.breakEven.applyToProfits === false) {
      return false;
    }
    if (totalPnL < 0 && preferences.tradeForm.breakEven.applyToLosses === false) {
      return false;
    }

    // Si le type de seuil est un pourcentage
    if (preferences.tradeForm.breakEven.thresholdType === 'percentage') {
      // The Journal percentage is based on the balance captured with this
      // trade. Use exactly that same reference for the Break Even threshold.
      const accountBalance = resolveBreakEvenBalance(trade, selectedAccount);
      if (accountBalance <= 0) {
        return false;
      }
      const percentageValue = Math.abs((totalPnL / accountBalance) * 100);

      if (totalPnL < 0) {
        // Pour les pertes, utiliser thresholdFrom
        return percentageValue <= (preferences.tradeForm.breakEven.thresholdFrom || 0);
      } else {
        // Pour les profits, utiliser thresholdTo
        return percentageValue <= (preferences.tradeForm.breakEven.thresholdTo || 0);
      }
    }
    // Si le type de seuil est un montant fixe
    else {
      if (totalPnL < 0) {
        // Pour les pertes, utiliser thresholdFrom
        return Math.abs(totalPnL) <= (preferences.tradeForm.breakEven.thresholdFrom || 0);
      } else {
        // Pour les profits, utiliser thresholdTo
        return totalPnL <= (preferences.tradeForm.breakEven.thresholdTo || 0);
      }
    }
  } catch (error) {
    // console.error supprimé
    // En cas d'erreur, revenir à la logique simple
    return isNearZeroPnL(getTradeNetPnL(trade));
  }
};

/**
 * Obtient la classe CSS pour le profit/loss
 */
export const getProfitClass = (trade: Trade, preferences?: any, selectedAccount?: any): string => {
  if (isBreakEven(trade, preferences, selectedAccount)) {
    return 'text-content-secondary';
  }
  return getTradeNetPnL(trade) > 0 ? 'text-emerald-500' : 'text-red-500';
};

/**
 * Formate une date pour l'affichage avec application du décalage horaire
 * @param dateString - Date au format ISO
 * @param offsetHours - Décalage horaire à appliquer (optionnel)
 */
const formatDateForDisplay = (dateString?: string, offsetHours: number = 0): string => {
  if (!dateString) return 'Date non disponible';

  try {
    // Pour les chaînes ISO, extraire directement les composants sans conversion
    const isoMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (isoMatch) {
      const [_, year, month, day, hoursStr, minutes] = isoMatch;
      let hours = parseInt(hoursStr, 10);
      
      // Appliquer le décalage horaire si fourni
      if (offsetHours !== 0) {
        hours = (hours + offsetHours + 24) % 24;
      }
      
      const hoursFormatted = String(hours).padStart(2, '0');
      return `${day}/${month}/${year} ${hoursFormatted}:${minutes}`;
    }
  } catch (error) {
    // console.error supprimé
  }

  return 'Date non disponible';
};

/**
 * Formate la date d'un trade avec application du décalage horaire
 * Gère correctement l'heure d'été/hiver via le vrai UTC et les fuseaux IANA.
 */
export const formatTradeDate = (trade: Trade, account?: any, prefGlobalTz?: string): string => {
  if (!trade.openDateTime) return 'Date non disponible';

  try {
    const isAutoTrade = (trade as any)?.source === 'auto';

    if (!isAutoTrade) {
      // Trades manuels : l'heure a été saisie par l'utilisateur en heure locale
      // → on l'affiche telle quelle, sans conversion (pas de timezone ambiguë à appliquer).
      const isoString = typeof trade.openDateTime === 'string' ? trade.openDateTime
        : typeof trade.openDateTime === 'object' && 'seconds' in trade.openDateTime
          ? (() => {
              const parsed = new Date((trade.openDateTime as any).seconds * 1000);
              return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
            })()
          : (() => {
              const parsed = trade.openDateTime as Date;
              return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed.toISOString() : '';
            })();
      const match = isoString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
      return 'Date non disponible';
    }

    // Trades auto (sync live + imports fichier) : conversion UTC réelle → IANA (DST-aware).
    const utcDate = getTradeUtcDate(trade, account);
    if (!utcDate) return 'Date non disponible';

    const globalTz = prefGlobalTz || (account as any)?.globalTimezone || '0';
    const iana = getIanaFromGlobalTimezone(globalTz);
    return formatToLocalDisplayString(utcDate, iana);

  } catch (error) {
    return 'Date non disponible';
  }
};

/**
 * Formate la date de fermeture d'un trade avec application du décalage horaire
 * Gère correctement l'heure d'été/hiver via le vrai UTC et les fuseaux IANA.
 */
export const formatTradeCloseDate = (trade: Trade, account?: any, prefGlobalTz?: string): string => {
  if (!trade.closeDateTime) return 'N/A';

  try {
    const isAutoTrade = (trade as any)?.source === 'auto';

    if (!isAutoTrade) {
      // Trades manuels : affichage brut de l'heure saisie (sans conversion).
      const isoString = typeof trade.closeDateTime === 'string' ? trade.closeDateTime
        : typeof trade.closeDateTime === 'object' && 'seconds' in trade.closeDateTime
          ? (() => {
              const parsed = new Date((trade.closeDateTime as any).seconds * 1000);
              return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
            })()
          : (() => {
              const parsed = trade.closeDateTime as Date;
              return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed.toISOString() : '';
            })();
      const match = isoString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
      return 'N/A';
    }

    // Trades auto : conversion UTC → IANA.
    const closeTradeObj = { ...trade, openDateTime: trade.closeDateTime };
    const utcDate = getTradeUtcDate(closeTradeObj, account);
    if (!utcDate) return 'N/A';

    const globalTz = prefGlobalTz || (account as any)?.globalTimezone || '0';
    const iana = getIanaFromGlobalTimezone(globalTz);
    return formatToLocalDisplayString(utcDate, iana);

  } catch (error) {
    return 'N/A';
  }
};

/**
 * Groupe les trades par mois
 * Les brouillons sont mis dans une catégorie spéciale "Brouillons"
 */
export const groupTradesByMonth = (trades: Trade[]): { [key: string]: Trade[] } => {
  return trades.reduce((acc: { [key: string]: Trade[] }, trade) => {
    // Les brouillons vont dans une catégorie spéciale
    if (trade.isDraft === true) {
      const draftKey = "__DRAFTS__";
      if (!acc[draftKey]) {
        acc[draftKey] = [];
      }
      acc[draftKey].push(trade);
      return acc;
    }

    const tradeDate = getTradeDate(trade);

    if (tradeDate && !isNaN(tradeDate.getTime())) {
      try {
        const monthKey = format(tradeDate, 'yyyy-MM');
        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }
        acc[monthKey].push(trade);
      } catch (e) {
        // En cas d'erreur de formatage, mettre dans "__UNDATED__"
        const monthKey = "__UNDATED__";
        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }
        acc[monthKey].push(trade);
      }
    } else {
      // Si la date est invalide ou null, ajouter à "__UNDATED__"
      const monthKey = "__UNDATED__";
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(trade);
    }
    return acc;
  }, {});
};

/**
 * Trie les groupes de trades par mois
 * Les brouillons sont toujours en premier, puis les mois par ordre décroissant, puis "Non daté" à la fin
 */
export const sortTradeGroups = (groups: [string, Trade[]][]): [string, Trade[]][] => {
  return groups.sort((a, b) => {
    // Les brouillons sont toujours en premier
    if (a[0] === "__DRAFTS__") return -1;
    if (b[0] === "__DRAFTS__") return 1;

    // "__UNDATED__" est toujours à la fin
    if (a[0] === "__UNDATED__") return 1;
    if (b[0] === "__UNDATED__") return -1;

    // Essayer de trier par date du premier trade de chaque mois
    const dateA = getTradeDate(a[1][0]);
    const dateB = getTradeDate(b[1][0]);

    // Si une des dates est null, la mettre à la fin
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * Obtient le timestamp d'un trade (priorité : openDateTime, date)
 */
export const getTradeTimestamp = (trade: Trade): number => {
  let timestamp = 0;

  try {
    // Essayer d'abord openDateTime
    if (trade.openDateTime) {
      if (typeof trade.openDateTime === 'object' && trade.openDateTime !== null && 'seconds' in trade.openDateTime) {
        return (trade.openDateTime as any).seconds * 1000;
      } else if (typeof trade.openDateTime === 'string' || typeof trade.openDateTime === 'number') {
        const date = new Date(trade.openDateTime);
        if (!isNaN(date.getTime())) return date.getTime();
      }
    }

    // Essayer ensuite date
    if (trade.date) {
      if (typeof trade.date === 'object' && trade.date !== null && 'seconds' in trade.date) {
        return (trade.date as any).seconds * 1000;
      } else if (typeof trade.date === 'string' || typeof trade.date === 'number') {
        const date = new Date(trade.date);
        if (!isNaN(date.getTime())) return date.getTime();
      }
    }
  } catch (error) {
    // console.error supprimé
  }

  return timestamp;
};
